"use client";
import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { usePrerender } from '../hooks/usePrerender';
import ContactModal from '../components/ContactModal';
import HomeHero from '../components/HomeHero';
import PrincipleCards from '../components/PrincipleCards';
import GeoNewsHomeSection from '../components/GeoNewsHomeSection';
import GeoKnowledgeHomeSection from '../components/GeoKnowledgeHomeSection';
import OptimizedImage from '../components/OptimizedImage';
import { getCaseDetailPath } from '../data/caseDetails';

// Mock 数据接口定义
interface CaseItem {
  image: string;
  tags: string[];
  url: string;
  title: string;
  description: string;
  stats: Record<string, string>;
  platforms: string[];
  focusTags?: string[];
  coverHasText?: boolean;
}

const platformsData = [
  {
    id: 'doubao',
    name: '豆包AI-GEO优化',
    title: '豆包AI-场景适配服务',
    desc: '豆包用户多在抖音、头条寻求生活化、娱乐化解答。GEO优化公司帮助企业梳理真实用户场景，构建问答内容矩阵；将企业真实服务案例转化为符合短视频/图文生态的FAQ；确保企业认证信息在娱乐场景下依然可被AI准确识别。',
    tags: ['真实用户场景梳理', '问答内容矩阵构建', '真实案例原生适配', '资质信息准确识别', '信息准确性保障']
  },
  {
    id: 'deepseek',
    name: 'DeepSeek-GEO优化',
    title: 'DeepSeek-专业领域合规优化',
    desc: 'DeepSeek用户多为深度技术/专业决策人群。GEO优化公司帮助您将企业真实技术白皮书、专利信息转化为机器可读格式；在CSDN、知乎等技术社区建立基于真实专业知识的品牌内容；确保AI引用技术观点时可追溯到原始权威出处。',
    tags: ['技术文档结构化', '专利信息标准化', '专业知识社区建设', '知识溯源机制', '真实技术实力呈现']
  },
  {
    id: 'yuanbao',
    name: '腾讯元宝-GEO优化',
    title: '腾讯元宝-微信生态合规优化',
    desc: '基于微信真实社交场景，GEO优化公司帮助您优化公众号历史文章结构，确保AI能准确提取企业服务范围；基于真实客户案例制作视频号内容；优化企业在微信搜一搜中的基础信息准确性（地址、电话、营业时间等）。',
    tags: ['公众号权威内容治理', '视频号真实案例展示', '基础信息准确性优化', '微信生态规范适配', '真实服务信息呈现']
  },
  {
    id: 'wenxin',
    name: '文心一言-GEO优化',
    title: '文心一言-百度生态合规优化',
    desc: '对接百度百科、百家号等权威信源，GEO优化公司将企业真实信息对接百度知识图谱（不编造，只对齐）；确保品牌信息在搜索结果中被准确呈现；优化多轮对话中企业信息的语义一致性。',
    tags: ['百度百科信息对齐', '知识图谱标准对接', '搜索语义准确性', '权威信源合规建设', '真实信息准确呈现']
  },
  {
    id: 'kimi',
    name: 'KIMI-GEO优化',
    title: 'KIMI-长文本合规优化',
    desc: '针对KIMI长文本处理能力，GEO优化公司帮助您构建深度报告与专业文献信源库；将企业真实技术资料提炼为结构化摘要；确保在长篇上下文中信息的连贯性和准确性。',
    tags: ['深度报告结构化', '专业文献信源库', '上下文信息连贯', '真实资料准确呈现', '复杂问题准确解答']
  },
  {
    id: 'qianwen',
    name: '通义千问-GEO优化',
    title: '通义千问-阿里生态合规优化',
    desc: '整合阿里电商与行业数据信源，GEO优化公司帮助您优化企业服务的多模态信息展示；确保在商品问答场景中信息准确；在商业决策场景下呈现真实的企业服务能力。',
    tags: ['电商信源合规整合', '行业数据准确对齐', '企业服务场景适配', '阿里生态规范遵循', '真实服务能力呈现']
  }
];

const platformLogos: Record<string, { src: string; alt: string; whitePlate?: boolean }> = {
  doubao: { src: '/platform-logos/doubao.png', alt: '豆包', whitePlate: true },
  deepseek: { src: '/platform-logos/deepseek.ico', alt: 'DeepSeek', whitePlate: true },
  yuanbao: { src: '/platform-logos/yuanbao.png', alt: '腾讯元宝' },
  wenxin: { src: '/platform-logos/wenxin.png', alt: '文心一言' },
  kimi: { src: '/platform-logos/kimi.png', alt: 'Kimi' },
  qianwen: { src: '/platform-logos/qianwen.png', alt: '通义千问' },
};

const caseValuePoints = [
  { title: '准确', desc: '信息准确性提升' },
  { title: '合规', desc: '权威信源建设' },
  { title: '真实', desc: '基于真实价值' },
];

const casePlatformTags = ['DeepSeek优化', '豆包优化', '元宝优化', '通义千问优化'];

const cleanCaseDescription = (description: string) =>
  description.replace(/标签\s*[:：]?/g, '').replace(/\s+/g, ' ').trim();

const renderPlatformIcon = (id: string) => {
  const logo = platformLogos[id];
  if (!logo) return null;

  return (
    <div className={`w-10 h-10 flex items-center justify-center ${logo.whitePlate ? 'bg-white rounded-lg p-1' : ''}`}>
      <OptimizedImage
        src={logo.src}
        alt={`${logo.alt}官方Logo`}
        className={`${logo.whitePlate ? 'w-full h-full' : 'w-9 h-9'} rounded-lg object-contain`}
      />
    </div>
  );
};

const Home: React.FC = () => {
  const [activePlatform, setActivePlatform] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [homeCases, setHomeCases] = useState<CaseItem[]>([]);

  // 触发预渲染事件
  usePrerender();

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  // 从 mock 数据加载首页案例（前6个）
  useEffect(() => {
    fetch('/mock/caselist.json')
      .then(response => response.json())
      .then(data => {
        setHomeCases(data.slice(0, 6)); // 只取前6个
      })
      .catch(error => {
        console.error('加载案例数据失败:', error);
      });
  }, []);

  // 图片种子映射
  const getImgSeed = (title: string) => {
    const seeds: Record<string, string> = {
      '佛山某智能包装设备厂AIGEO案例': 'factory',
      '广州某文创礼品厂AIGEO案例': 'gift',
      '东莞某智能穿戴设备厂GEO案例': 'smartwatch',
      '2025年深圳宠物服务店GEO案例': 'pet',
      '体系致胜AIGEO-2025年化妆品行业GEO案例': 'makeup',
      '体系致胜AIGEO-2025年制造业GEO案例': 'industry',
      '体系致胜AIGEO-2025年服装制造GEO案例': 'fashion',
      '体系致胜AIGEO-2025年别墅园林设计GEO案例': 'garden',
      '体系致胜AIGEO-2025年玩具批发行业GEO案例': 'toy',
      '体系致胜AIGEO-2025年移民服务GEO案例': 'immigration',
      '体系致胜AIGEO-2025年家政服务GEO案例': 'housekeeping',
      '体系致胜AIGEO-2025年商务服务业GEO案例': 'business',
    };
    return seeds[title] || 'geo';
  };

  const getDisplayCaseTitle = (title: string) => (
    title.replace(/^体系致胜AIGEO-2025年/, '').trim()
  );

  const getCaseImageSrc = (title: string) => {
    const images: Record<string, string> = {
      '装备制造行业GEO案例': '/case-images/equipment-geo/00-hero.jpg',
      '银行金融行业GEO案例': '/case-details/bank-geo/00-hero.jpg',
      '律师行业GEO案例': '/case-images/law-geo.jpg',
      '信创软件行业GEO案例': '/case-images/enterprise-software.jpg',
      '佛山某智能包装设备厂AIGEO案例': '/case-images/packaging-factory.png',
      '广州某文创礼品厂AIGEO案例': '/case-images/creative-gift-factory.png',
      '东莞某智能穿戴设备厂GEO案例': '/case-images/smart-wearable-lab.png',
      '2025年深圳宠物服务店GEO案例': '/case-images/pet-grooming-salon.png',
      '体系致胜AIGEO-2025年化妆品行业GEO案例': '/case-images/cosmetics-store.png',
      '体系致胜AIGEO-2025年制造业GEO案例': '/case-images/manufacturing-center.png',
      '体系致胜AIGEO-2025年服装制造GEO案例': '/case-images/garment-factory.png',
    };
    return images[title] || 'https://picsum.photos/seed/' + getImgSeed(title) + '/800/450';
  };

  return (
    <div className="flex flex-col italic-none">
      <SEO 
        title="前排科技GEO_专业合规GEO优化服务" 
        description="前排科技是一家专门提供GEO优化服务的专业服务商，帮助客户使其品牌、产品或服务在AI平台上得到优先推荐"
        keywords="前排科技,GEO,AI营销,专业合规"
      />
      
      <HomeHero onConsult={openModal} />

      {/* Four Steps Section */}
      <section className="py-16 bg-[#F8F8F8]">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl font-bold text-[#111827] tracking-tight">合规GEO优化四步工作法</h2>
            <p className="text-[#667085] text-lg font-medium tracking-wide">GEO优化公司配合基于真实企业价值介绍，通过结构化治理帮助AI准确理解</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {[
              {
                id: 'D',
                title: '企业价值发现',
                desc: 'Discovery',
                intro: 'GEO优化公司会深度梳理企业真实竞争优势、资质认证、客户案例，盘点全网可验证的真实数据。',
                points: ['全业务商务认证与建模', '真实客户案例采集', '全网存量数据精算审计', '核心竞争优势提炼']
              },
              {
                id: 'S',
                title: '结构化治理',
                desc: 'Structuring',
                intro: 'GEO优化公司会按AI推荐标准重构企业信息，对接权威知识库，确保AI准确理解业务类型。',
                points: ['AI类搜索引擎标准库构建', '知识对象数据结构化', '多端多平台内容一致性', '机器可理解模式优化']
              },
              {
                id: 'G',
                title: '内容合规治理',
                desc: 'Governance',
                intro: 'GEO优化公司会在合规平台发布基于真实业务的专业内容，协助展示真实客户评价，拒绝刷评。',
                points: ['权威合规中心内容建设', '真实资产评价管理', '专业内容自营分发布', '虚假信息主动清理']
              },
              {
                id: 'M',
                title: '合规监测',
                desc: 'Monitoring',
                intro: 'GEO优化公司会追踪AI模型对企业信息的引用准确性，及时发现并纠正错误信息，维护品牌纯净度。',
                points: ['AI引用准确性监测', '错误信息及时纠正', '虚假关联风险预警', '品牌网络声誉维护']
              }
            ].map((step) => (
              <div key={step.id} className="relative p-10 border border-[#E8E8E8] rounded-2xl hover:border-[#D90000]/25 hover:shadow-[0_20px_45px_rgba(120,0,0,0.10)] transition-all group bg-white hover:-translate-y-2">
                <div className="flex flex-col items-center">
                   <div className="text-7xl font-black text-[#D90000]/5 transition-colors group-hover:text-[#D90000]/10 select-none">{step.id}</div>
                   <h3 className="text-2xl font-bold text-[#111827] -mt-8">{step.title}</h3>
                   <span className="text-[10px] text-[#D90000]/55 tracking-[0.3em] font-bold uppercase mt-2">{step.desc}</span>
                </div>
                <div className="w-12 h-0.5 bg-[#F4B8B8] mx-auto my-6"></div>
                <p className="h-[72px] mb-6 text-sm text-slate-600 leading-relaxed text-center flex items-center justify-center">
                  {step.intro}
                </p>
                <ul className="space-y-4">
                  {step.points.map((p, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-slate-600 leading-relaxed">
                      <div className="w-4 h-4 mt-0.5 rounded border border-[#D90000]/20 bg-[#FFF1F1] flex items-center justify-center text-[10px] text-[#D90000] shrink-0">✓</div>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Principles */}
      <section className="py-16 bg-[#F2F3F5] border-t border-[#E8E8E8]">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl font-bold text-[#111827] tracking-tight">我们的合规底线</h2>
            <p className="text-[#667085] text-lg font-medium tracking-wide">前排科技 · 坚决抵制黑帽GEO手法，坚守真实信源原则</p>
          </div>
          
          <PrincipleCards />

          {/* Risk Warning Box */}
          <div className="mt-16 max-w-[1240px] mx-auto min-h-[180px] bg-[#FFF4F4] border border-[#F1B8BC] p-8 md:p-10 rounded-xl">
            <div className="flex items-center gap-3">
              <span className="text-2xl leading-none">⚠️</span>
              <h4 className="text-[#D32F36] font-extrabold text-xl">风险警示</h4>
            </div>
            <p className="mt-4 text-[#A9272E] text-sm md:text-base leading-8">
              我们坚决抵制"关键词堆砌"、"虚假评论生成"、"数据刷量"等黑帽GEO手法，此类行为已被315晚会曝光并面临平台严厉打击。 采用黑帽GEO可能导致：AI平台永久屏蔽企业信息、虚假宣传行政处罚（依据《反不正当竞争法》）、品牌声誉不可逆损害。<strong>选择合规服务，是对企业长期品牌的保护。</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section data-platform-section className="py-16 bg-[#f8fafc] overflow-hidden">
        <div className="max-w-[1200px] mx-auto px-6">
          <div data-platform-header className="text-center mb-16 space-y-4">
            <h2 className="text-4xl font-bold text-slate-900 tracking-tight">国内6大AI平台-合规GEO服务</h2>
            <p className="text-slate-500 text-xl">适配各平台模型特性，让企业真实优势被准确呈现</p>
          </div>
          
          {/* Platform Tabs */}
          <div data-platform-tabs className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {platformsData.map((plat, idx) => {
              const isActive = activePlatform === idx;
              return (
                <div 
                  key={idx}
                  onClick={() => setActivePlatform(idx)}
                  className={`flex flex-col items-center justify-center w-full h-[100px] rounded-xl cursor-pointer transition-all ${isActive ? 'bg-gradient-to-r from-[#D83A40] to-[#B7262D] text-white shadow-[0_10px_26px_rgba(183,38,45,0.24)]' : 'bg-white text-slate-700 shadow-sm hover:shadow-md'}`}
                >
                  <div className="mb-2.5">
                    {renderPlatformIcon(plat.id)}
                  </div>
                  <div className={`text-[13px] ${isActive ? 'font-medium' : 'font-normal'}`}>{plat.name}</div>
                </div>
              );
            })}
          </div>

          {/* Platform Content */}
          <div data-platform-content className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 relative overflow-hidden">
            <AnimatePresence mode="wait">
               <motion.div
                 key={activePlatform}
                 initial={{ opacity: 0, y: 10 }}
                 animate={{ opacity: 1, y: 0 }}
                 exit={{ opacity: 0, y: -10 }}
                 transition={{ duration: 0.3 }}
                 className="space-y-6"
               >
                 <h3 data-platform-title className="text-2xl md:text-[26px] font-bold text-slate-900 tracking-tight">{platformsData[activePlatform].title}</h3>
                 <p data-platform-description className="text-slate-600 leading-relaxed text-[15px] max-w-[1000px]">
                   {platformsData[activePlatform].desc}
                 </p>
                 <div data-platform-tags className="flex flex-wrap gap-x-4 gap-y-3">
                   {platformsData[activePlatform].tags.map((tag, i) => (
                      <div key={i} className="flex items-center gap-1.5 bg-slate-50 border border-slate-100 text-slate-600 px-3 py-1.5 rounded text-[13px]">
                        <span className="text-slate-400 text-xs">✓</span>
                        {tag}
                      </div>
                   ))}
                 </div>
               </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-geo-cta className="py-12 bg-gradient-to-r from-[#D83A40] to-[#B7262D] text-white">
        <div data-geo-cta-inner className="max-w-[1200px] mx-auto px-6 flex flex-col md:flex-row justify-center items-center gap-10 md:gap-16">
          <div data-geo-cta-copy className="space-y-4">
            <h2 className="text-4xl font-bold">准备开始企业的合规GEO优化之旅了吗？</h2>
            <p className="text-white/80 text-xl font-medium tracking-wide">基于企业真实价值，让AI准确理解和推荐您的商业优势</p>
          </div>
          <div data-geo-cta-actions className="flex gap-4">
            <button onClick={openModal} className="bg-white text-[#B7262D] font-extrabold py-4 px-10 rounded-xl hover:bg-[#FFF4F4] transition-all active:scale-95 shadow-[0_10px_24px_rgba(88,12,18,0.22)] inline-block">
              免费咨询
            </button>
            <Link to="/compliance" onClick={() => window.scrollTo(0, 0)} className="bg-[#16181D]/35 text-white border border-white/45 font-extrabold py-4 px-10 rounded-xl hover:bg-[#16181D]/55 hover:border-white/70 transition-all active:scale-95 shadow-[0_10px_24px_rgba(88,12,18,0.18)] inline-block">
              查看合规声明
            </Link>
          </div>
        </div>
      </section>
      
      {/* Case Studies Section */}
      <section data-case-section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-[1400px] mx-auto px-6">
          <div data-case-header className="text-center mb-16 space-y-4">
            <h2 className="text-4xl font-bold text-slate-900 tracking-tight">GEO优化典型案例</h2>
            <p data-case-subtitle className="text-slate-500 text-xl">基于真实企业价值的合规优化实践，展示AI信息准确性提升成果</p>
          </div>
          
          <div data-case-summary className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-16 text-center bg-white rounded-xl shadow-md p-6 ">
            {[
              { title: '合规', desc: '真实合规信源原则' },
              { title: '覆盖', desc: '国流6大AI平台全覆盖' },
              { title: '准确', desc: '产品品牌信息呈现准确' },
              { title: '可追溯', desc: '所有技术可审计' }
            ].map((item, i) => (
              <div key={i} className="space-y-2">
                <div data-case-summary-title className="text-2xl font-bold text-[#B7262D]">{item.title}</div>
                <div data-case-summary-desc className="text-xs text-slate-500">{item.desc}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {homeCases.map((item, idx) => (
              <Link
                key={idx}
                data-case-card-link
                to={getCaseDetailPath(item.title)}
                aria-label={`查看${item.title}详情`}
                className="block h-full rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D83A40] focus-visible:ring-offset-4"
              >
                <motion.article
                  data-case-card
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="h-full cursor-pointer bg-white rounded-2xl overflow-hidden shadow-[0_8px_24px_rgba(16,24,40,0.08)] hover:shadow-[0_16px_34px_rgba(88,12,18,0.13)] transition-all border border-[#E8E8E8] hover:border-[#D83A40]/35 group flex flex-col"
                >
                {/* Image Header */}
                <div className="relative aspect-[2/1] overflow-hidden bg-[#16181D]">
                  <OptimizedImage
                    src={getCaseImageSrc(item.title)}
                    alt={item.title}
                    className={`h-full w-full transition-transform duration-500 ${item.coverHasText ? "object-contain bg-[#E8F2FA]" : "object-cover opacity-70 group-hover:scale-105"}`}
                    referrerPolicy="no-referrer"
                  />
                  {!item.coverHasText && <>
<div className="absolute inset-0 bg-gradient-to-t from-[#23080B]/90 via-[#4B1015]/25 to-black/10"></div>
                  
                  {/* Overlay Text */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-white text-center">
                    <div className="text-3xl font-black tracking-wide drop-shadow-lg mb-1">
                      前排科技 AIGEO
                    </div>
                    <div className="text-sm font-medium tracking-wide text-white/90 drop-shadow-md">
                      行业合规GEO案例
                    </div>
                  </div>

                  {/* Bottom Tags */}
                  <div className="absolute bottom-3 left-3 flex gap-2">
                    {['GEO优化运营', 'GEO推广服务'].map((tag) => (
                      <span data-case-service-tag key={tag} className="bg-white/95 backdrop-blur text-[#16181D] border border-white/70 text-[10px] font-medium px-2.5 py-1 rounded-full">{tag}</span>
                    ))}
                  </div>
                </>}
</div>

                {/* Card Body */}
                <div className="p-5 flex flex-col flex-grow">
                  <h3 className="font-bold text-slate-900 text-lg leading-tight mb-2 line-clamp-1 group-hover:text-[#B7262D] transition-colors">{getDisplayCaseTitle(item.title)}</h3>
                  <p data-case-description className="text-xs text-[#667085] mb-4 flex-grow line-clamp-1">{cleanCaseDescription(item.description)}</p>

                  {/* Brand Value Points */}
                  <div data-case-value-points className="grid grid-cols-3 gap-2 pb-4 mb-4 border-b border-[#F0D9DB]">
                    {caseValuePoints.map((point) => (
                      <div key={point.title} className="text-center">
                        <div className="text-lg font-black text-[#B7262D]">{point.title}</div>
                        <div data-case-value-desc className="text-xs text-slate-500 mt-1">{point.desc}</div>
                      </div>
                    ))}
                  </div>

                  {/* Platform Tags */}
                  <div data-case-platforms className="grid grid-cols-4 gap-2">
                    {(item.focusTags ?? casePlatformTags).map((platform, platformIdx) => {
                      const colors = ['#7E171D', '#D83A40', '#B7262D', '#16181D'];
                      return (
                        <span key={platform} style={{ backgroundColor: colors[platformIdx] }} className="w-full text-center text-white text-[10px] font-bold px-1.5 py-1 rounded-full">{platform}</span>
                      );
                    })}
                  </div>
                </div>
                </motion.article>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Intelligence & News Section */}
      <GeoNewsHomeSection />
      {false && (
      <section data-news-section className="py-16 bg-white border-t border-slate-100">
        <div className="max-w-[1400px] mx-auto px-6">
          <div data-news-header className="relative mb-16 border-b border-slate-100 pb-8 text-center">
            <div>
               <h2 className="text-4xl font-bold text-slate-900 tracking-tight">GEO优化行业资讯</h2>
               <p className="text-slate-500 mt-2">汇集GEO行业报告、深度洞察与算法情报，赋能企业AI营销</p>
            </div>
            <Link to="/knowledge" className="mt-4 inline-block text-blue-600 font-bold hover:underline md:mt-0 md:absolute md:right-0 md:bottom-8">更多 →</Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-20">
             {[
               { title: '美国是如何处理"GEO优化投毒的"-美国合规GEO监管解读与合规内容优化发展方向', desc: '"GEO投毒"问题的全球共识-这并非中国独有的问题。在美国，类似行为被称为"黑帽GEO"、"RAG投...', date: '2026-03-31', tag: 'GEO报告', imgSeed: 'report1' },
               { title: '艾瑞咨询《2026 年 GEO 生成式引擎优化行业研究报告》解读', desc: '核心定位：AI 搜索时代的营销底层变革GEO（生成式引擎优化）作为 AI 时代新兴的营销优化策略，核...', date: '2026-02-10', tag: '行业报告', imgSeed: 'report2' },
               { title: '易观分析《中国GEO行业市场发展报告2026》解读', desc: '易观分析于2026年1月发布的《中国GEO行业市场发展报告2026》，系统揭示了生成式引擎优化（GE...', date: '2026-01-30', tag: '行业洞察', imgSeed: 'report3' },
               { title: '2025中国GEO趋势与品牌增长策略研报解读', desc: '变革催生了全新营销范式GEO（生成式引擎优化），与传统SEO、社媒种草形成本...', date: '2025-11-07', tag: '趋势报告', imgSeed: 'report4' }
             ].map((n, i) => (
               <div key={i} className="group cursor-pointer">
                  <div className="aspect-[4/5] bg-slate-900 rounded-xl mb-4 overflow-hidden relative">
                    <img
                      src={`https://picsum.photos/seed/${n.imgSeed}/400/500`}
                      alt={n.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-60"
                      referrerPolicy="no-referrer"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent p-6 flex flex-col justify-end">
                       <span className="text-[10px] text-blue-400 font-bold uppercase mb-2">● {n.tag}</span>
                       <h4 className="text-white font-bold leading-tight group-hover:text-blue-200 transition-colors text-sm">{n.title}</h4>
                    </div>
                  </div>
                  <div className="text-[10px] text-slate-400">{n.date}</div>
               </div>
             ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
             <div className="space-y-8">
                <div className="flex justify-between items-center border-b border-slate-200 pb-4">
                   <h5 className="font-bold text-slate-900 flex items-center gap-2">💡 GEO洞察</h5>
                   <Link to="#" className="text-xs text-blue-600">更多 →</Link>
                </div>
                <ul className="space-y-6">
                   {[
                     '正本清源：315曝光黑帽GEO洗礼当下，前排GEO以合规引领行业重塑',
                     '45亿红包结战全网 AI时代：2026 春节六大平台活动重塑企业使用习惯',
                     '通义千问春节活动深度解析：1.3亿订单背后，消费AI Agent的爆发与行业重构',
                     'AI洞察：豆包如何在2026年春节期间视频流量优势和场景数据',
                     '元宝春节 10亿红包收官：DAU峰值9000万，AI+社交春节节日流量重塑',
                     '深刻解读搜索场景 GEO 案例展示的 4 大监测趋势'
                   ].map((item, i) => (
                     <li key={i} className="text-sm text-slate-600 hover:text-blue-600 cursor-pointer transition-colors border-b border-slate-50 pb-4 last:border-0">{item}</li>
                   ))}
                </ul>
             </div>
             <div className="space-y-8">
                <div className="flex justify-between items-center border-b border-slate-200 pb-4">
                   <h5 className="font-bold text-slate-900 flex items-center gap-2">📢 GEO情报</h5>
                   <Link to="#" className="text-xs text-blue-600">更多 →</Link>
                </div>
                <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100">
                  <ul className="space-y-6">
                    {[
                      'DeepSeek 算法迭代情况V3-D324与V4版本对RAG与GEO优化的差异',
                      '2026年3月 AI 搜索模型算法演变更新策略：回315时代下的低质侵权权威解构',
                      'Kimi 2026年春节期间短视频搜索推荐情况及监测情况',
                      'DeepSeek 2026年春节期间合规监测情况及优化合规',
                      '文心一言 2026年春节推荐详情及监测数据分析',
                      '通义千问 2026年春节期间搜索推荐及合规情况',
                      '豆包 2026年春节场景搜索模型监测视频情况'
                    ].map((item, i) => (
                      <li key={i} className="text-sm text-slate-600 hover:text-blue-600 cursor-pointer transition-colors border-b border-slate-200 pb-4 last:border-0">{item}</li>
                    ))}
                  </ul>
                </div>
             </div>
          </div>
        </div>
      </section>
      )}

      {/* Final Knowledge Base Section */}
      <GeoKnowledgeHomeSection />
      {false && (
      <section className="py-28 bg-[#F8FAFC]">
        <div className="max-w-[1400px] mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-slate-900 tracking-tight mb-4">GEO知识库</h2>
          <p className="text-slate-500 text-xl mb-20 font-medium italic">提供最新的GEO相关知识、行业资讯和技术文章，帮助企业了解最新的GEO优化趋势</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
             {[
               { title: '2026年4月豆包R4G算法深度解析：GEO优化的风险与机遇', date: '2026-04-08 23:25:57', tag: 'GEO实战方案' },
               { title: '2026年3月DeepSeek算法升级深度解读：GEO优化的“分水岭”时代已到', date: '2026-03-31 00:06:50', tag: '专家观点' },
               { title: 'DeepSeek算法回测与结果重构-2026年GEO生态系：GEO优化合规性研究报告', date: '2026-03-23 19:13:24', tag: 'GEO监测报告' }
             ].map((k, i) => (
               <div key={i} className="bg-white p-6 rounded-2xl border border-slate-200 text-left space-y-4 hover:shadow-xl transition-all group">
                  <div className="aspect-video bg-[#021021] rounded-xl flex items-center justify-center p-8 text-white relative">
                     <div className="absolute top-4 left-4 bg-blue-600 text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-widest">{k.tag}</div>
                     <div className="text-center group-hover:scale-105 transition-transform">
                        <div className="text-xl font-black italic mb-1 tracking-tighter">DeepSeek</div>
                        <div className="text-[10px] opacity-60 uppercase mb-2">深度解读</div>
                        <div className="text-3xl font-black">{i === 1 ? '算法演进与优化' : '2026年GEO生态'}</div>
                     </div>
                  </div>
                  <h4 className="font-bold text-slate-900 group-hover:text-blue-700 transition-colors">{k.title}</h4>
                  <p className="text-xs text-slate-500 leading-relaxed line-clamp-2">针对最新算法调整，前排GEO第一时间完成了算法回查和测试，为您提供深度优化建议...</p>
                  <div className="flex justify-between items-center pt-6 border-t border-slate-50 text-[10px]">
                     <span className="text-slate-400 font-mono tracking-tighter">{k.date}</span>
                     <span className="text-blue-700 font-bold bg-blue-50 px-3 py-1 rounded-full group-hover:bg-blue-700 group-hover:text-white transition-all">阅读更多</span>
                  </div>
               </div>
             ))}
          </div>
          
          <button className="border-2 border-blue-700 text-blue-700 hover:bg-blue-700 hover:text-white px-12 py-4 rounded-xl font-black text-sm transition-all active:scale-95 shadow-lg tracking-widest">
             查看更多GEO优化知识
          </button>
        </div>
      </section>
      )}

      {/* Bottom CTA */}
      <section data-geo-cta className="py-12 bg-gradient-to-r from-[#D83A40] to-[#B7262D] text-white">
        <div data-geo-cta-inner className="max-w-[1200px] mx-auto px-6 flex flex-col md:flex-row justify-center items-center gap-10 md:gap-16">
          <div data-geo-cta-copy className="space-y-4 text-center md:text-left">
            <h2 className="text-4xl font-bold">准备开始企业的合规GEO优化之旅了吗？</h2>
            <p className="text-white/80 text-xl font-medium tracking-wide">基于企业真实价值，让AI准确理解和推荐您的商业优势</p>
          </div>
          <div data-geo-cta-actions className="flex gap-4">
            <button onClick={openModal} className="bg-white text-[#B7262D] font-extrabold py-4 px-10 rounded-xl hover:bg-[#FFF4F4] transition-all active:scale-95 shadow-[0_10px_24px_rgba(88,12,18,0.22)] inline-block">
              免费咨询
            </button>
            <Link to="/compliance" className="bg-[#16181D]/35 text-white border border-white/45 font-extrabold py-4 px-10 rounded-xl hover:bg-[#16181D]/55 hover:border-white/70 transition-all active:scale-95 shadow-[0_10px_24px_rgba(88,12,18,0.18)] inline-block">
              查看合规声明
            </Link>
          </div>
        </div>
      </section>

      {/* 联系模态框 */}
      <ContactModal isOpen={isModalOpen} onClose={closeModal} />
    </div>
  );
};

export default Home;
